import * as React from 'react';
import { useMemo, useState } from 'react';
import { FormGroup, Label, Input, Alert } from 'reactstrap';
import { Box, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { IconButton } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/DeleteRounded';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { ConsentManagementSettingsData, ConsentsCheckboxSettingsData, Consents } from '../../../models';
import { CONSENT_LABEL_MAP } from '../../../shared/consentUtils';
import styled from 'styled-components';
import { AutocompleteTextField } from '../../../components/Forms/fields';

const EditableText = styled('div')`
    cursor: pointer;
    padding: var(--spacer-XS);
    border-radius: 4px;
    transition: all 0.2s;
    min-height: 30px;
    line-height: 1.6;

    &:hover {
        background-color: var(--bg-light);
        outline: 1px dashed var(--appBlue);
    }

    em {
        color: var(--text-placeholder);
    }
`;

const LinkPreview = styled('a')`
    color: var(--appBlue);
    text-decoration: none;
    cursor: pointer;
    margin-left: var(--spacer-XXS);
    padding: var(--spacer-XXS) var(--spacer-XS);
    border-radius: 3px;
    transition: all 0.2s;
    font-size: 0.9rem;

    &:hover {
        background-color: var(--bg-hover-light);
        text-decoration: underline;
    }
`;

interface Props {
    values: ConsentManagementSettingsData;
    onChange: (values: ConsentManagementSettingsData) => void;
}

const ConsentManagementSettings: React.FC<Props> = ({ values, onChange }) => {
    const t = useTranslations();

    // Initialize with 2 default checkboxes if empty
    const [localValues, setLocalValues] = useState<ConsentManagementSettingsData>(() => {
        if (values?.consentsCheckboxes?.length === 0 || !values?.consentsCheckboxes) {
            return {
                termsAndConditionsLink: values?.termsAndConditionsLink || '',
                privacyPolicyLink: values?.privacyPolicyLink || '',
                consentsCheckboxes: [
                    {
                        name: 'Essential',
                        consents: {
                            gdpr: true,
                            healthDataProcessing: true,
                            emailReminders: true,
                            smsReminders: true,
                            whatsAppReminders: true,
                            dataSharingWithProfessionals: true,
                            emailMarketing: false,
                            smsMarketing: false,
                            birthdayMessage: true,
                            thirdPartyDataSharing: false,
                            imageOrVideoCaptureForInstitutionalPurposes: false
                        },
                        isMandatory: true,
                        text: t(TK.essentialConsentsCheckboxText),
                        links: {}
                    },
                    {
                        name: 'Marketing',
                        consents: {
                            gdpr: false,
                            healthDataProcessing: false,
                            emailReminders: false,
                            smsReminders: false,
                            whatsAppReminders: false,
                            dataSharingWithProfessionals: false,
                            emailMarketing: true,
                            smsMarketing: true,
                            birthdayMessage: true,
                            thirdPartyDataSharing: true,
                            imageOrVideoCaptureForInstitutionalPurposes: false
                        },
                        isMandatory: false,
                        text: t(TK.marketingConsentsCheckboxText),
                        links: {}
                    }
                ]
            };
        }
        return values;
    });

    const [editingText, setEditingText] = useState<number | null>(null);
    const [editingLink, setEditingLink] = useState<{ checkboxIndex: number; linkKey: string } | null>(null);

    React.useEffect(() => {
        onChange(localValues);
    }, [localValues, onChange]);

    const handleFieldChange = (field: keyof ConsentManagementSettingsData, value: any) => {
        setLocalValues({
            ...localValues,
            [field]: value
        });
    };

    const handleCheckboxChange = (index: number, field: keyof ConsentsCheckboxSettingsData, value: any) => {
        const newCheckboxes = [...localValues.consentsCheckboxes];
        newCheckboxes[index] = {
            ...newCheckboxes[index],
            [field]: value
        };
        handleFieldChange('consentsCheckboxes', newCheckboxes);
    };

    const handleLinksChange = (index: number, oldKey: string, newKey: string, value: string) => {
        const newCheckboxes = [...localValues.consentsCheckboxes];
        const currentLinks = { ...(newCheckboxes[index].links || {}) };

        // If key changed, remove old key
        if (oldKey !== newKey && oldKey) {
            delete currentLinks[oldKey];
        }

        // Set new value
        if (newKey) {
            currentLinks[newKey] = value;
        }

        newCheckboxes[index] = {
            ...newCheckboxes[index],
            links: currentLinks
        };
        handleFieldChange('consentsCheckboxes', newCheckboxes);
    };

    const removeLinkField = (checkboxIndex: number, linkKey: string) => {
        const newCheckboxes = [...localValues.consentsCheckboxes];
        const currentLinks = { ...(newCheckboxes[checkboxIndex].links || {}) };
        delete currentLinks[linkKey];
        newCheckboxes[checkboxIndex] = {
            ...newCheckboxes[checkboxIndex],
            links: currentLinks
        };
        handleFieldChange('consentsCheckboxes', newCheckboxes);
    };

    const addLinkToCheckbox = (checkboxIndex: number) => {
        const newKey = `Link ${Object.keys(localValues.consentsCheckboxes[checkboxIndex].links || {}).length + 1}`;
        const linkIndex = Object.keys(localValues.consentsCheckboxes[checkboxIndex].links || {}).length;
        const linkId = `${checkboxIndex}-${linkIndex}`;
        handleLinksChange(checkboxIndex, '', newKey, '');
        setEditingLink({ checkboxIndex, linkKey: linkId });
    };

    const consentsMap = useMemo(() => ({
        gdpr: t(TK.consentGDPR),
        healthDataProcessing: t(TK.consentHealthDataProcessing),
        emailMarketing: t(TK.consentEmailMarketing),
        smsMarketing: t(TK.consentSMSMarketing),
        birthdayMessage: t(TK.consentBirthdayMessage),
        emailReminders: t(TK.consentEmailReminders),
        smsReminders: t(TK.consentSMSReminders),
        whatsAppReminders: t(TK.consentWhatsAppReminders),
        dataSharingWithProfessionals: t(TK.consentDataSharingWithProfessionals),
        thirdPartyDataSharing: t(TK.consentThirdPartyDataSharing),
        imageOrVideoCaptureForInstitutionalPurposes: t(TK.consentImageOrVideoCaptureForInstitutionalPurposes),
        [t(TK.consentGDPR)]: 'gdpr',
        [t(TK.consentHealthDataProcessing)]: 'healthDataProcessing',
        [t(TK.consentEmailMarketing)]: 'emailMarketing',
        [t(TK.consentSMSMarketing)]: 'smsMarketing',
        [t(TK.consentBirthdayMessage)]: 'birthdayMessage',
        [t(TK.consentEmailReminders)]: 'emailReminders',
        [t(TK.consentSMSReminders)]: 'smsReminders',
        [t(TK.consentWhatsAppReminders)]: 'whatsAppReminders',
        [t(TK.consentDataSharingWithProfessionals)]: 'dataSharingWithProfessionals',
        [t(TK.consentThirdPartyDataSharing)]: 'thirdPartyDataSharing',
        [t(TK.consentImageOrVideoCaptureForInstitutionalPurposes)]: 'imageOrVideoCaptureForInstitutionalPurposes',
    }), [t])

    const allConsentKeys = useMemo(() => Object.keys(CONSENT_LABEL_MAP) as Array<keyof Consents>, [])

    const addCheckbox = () => {
        const newCheckboxes = [...localValues.consentsCheckboxes];
        newCheckboxes.push({
            name: t(TK.consentDefaultName),
            consents: {
                gdpr: false,
                healthDataProcessing: false,
                emailReminders: false,
                smsReminders: false,
                whatsAppReminders: false,
                dataSharingWithProfessionals: false,
                emailMarketing: false,
                smsMarketing: false,
                birthdayMessage: false,
                thirdPartyDataSharing: false,
                imageOrVideoCaptureForInstitutionalPurposes: false
            },
            isMandatory: false,
            text: '',
            links: {}
        });
        handleFieldChange('consentsCheckboxes', newCheckboxes);
    };

    const removeCheckbox = (index: number) => {
        const newCheckboxes = localValues.consentsCheckboxes.filter((_, i) => i !== index);
        handleFieldChange('consentsCheckboxes', newCheckboxes);
    };

    const renderCheckboxItem = (checkbox: ConsentsCheckboxSettingsData, index: number) => {
        const hasText = checkbox.text && checkbox.text.trim().length > 0;
        const hasLinks = checkbox.links && Object.keys(checkbox.links).length > 0;
        const isEditingThisText = editingText === index;

        return (
            <Box
                key={index}
                sx={{
                    marginBottom: 'var(--spacer-M)',
                    paddingBottom: 'var(--spacer-M)',
                    borderBottom: '1px solid var(--appLigthGrey)'
                }}
            >
                {/* Checkbox preview with text */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacer-S)', marginBottom: 'var(--spacer-S)' }}>
                    <Checkbox
                        disabled
                        checked={false}
                        sx={{ padding: 0, margin: 0, marginTop: '2px' }}
                    />
                    <Box sx={{ flex: 1 }}>
                        {isEditingThisText ? (
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={checkbox.text || ''}
                                onChange={(e) => handleCheckboxChange(index, 'text', e.target.value)}
                                onBlur={() => setEditingText(null)}
                                placeholder={t(TK.checkboxTextPlaceholder)}
                                variant="standard"
                                autoFocus
                            />
                        ) : (
                            <EditableText onClick={() => setEditingText(index)}>
                                {hasText ? (
                                    <span>
                                        {checkbox.text}
                                        {checkbox.isMandatory && <span style={{ color: 'var(--danger-color)', marginLeft: 'var(--spacer-XXS)' }}>*</span>}
                                    </span>
                                ) : (
                                    <em>{t(TK.clickToEdit)}</em>
                                )}
                            </EditableText>
                        )}

                        {/* Links preview */}
                        {hasLinks && (
                            <Box sx={{ marginTop: 'var(--spacer-XXS)' }}>
                                {Object.entries(checkbox.links).map(([label, url], linkIndex) => {
                                    const linkId = `${index}-${linkIndex}`;
                                    const isEditingThisLink = editingLink?.checkboxIndex === index && editingLink?.linkKey === linkId;

                                    if (isEditingThisLink) {
                                        return (
                                            <Box key={linkId} sx={{ display: 'flex', gap: 'var(--spacer-XS)', alignItems: 'center', marginBottom: 'var(--spacer-XXS)' }}>
                                                <TextField
                                                    size="small"
                                                    value={label}
                                                    onChange={(e) => {
                                                        const newLabel = e.target.value;
                                                        handleLinksChange(index, label, newLabel, url);
                                                    }}
                                                    placeholder={t(TK.linkLabelPlaceholder)}
                                                    sx={{ width: '150px' }}
                                                />
                                                <TextField
                                                    size="small"
                                                    type="url"
                                                    value={url}
                                                    onChange={(e) => handleLinksChange(index, label, label, e.target.value)}
                                                    placeholder={t(TK.linkUrlPlaceholder)}
                                                    sx={{ flex: 1 }}
                                                    autoFocus
                                                />
                                                <IconButton
                                                    size="small"
                                                    disableRipple
                                                    onClick={() => {
                                                        removeLinkField(index, label);
                                                        setEditingLink(null);
                                                    }}
                                                    style={{ color: 'var(--danger-color)' }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    disableRipple
                                                    onClick={() => setEditingLink(null)}
                                                >
                                                    ✓
                                                </IconButton>
                                            </Box>
                                        );
                                    }

                                    return (
                                        <LinkPreview
                                            key={label}
                                            href={url || '#'}
                                            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                                e.preventDefault();
                                                setEditingLink({ checkboxIndex: index, linkKey: linkId });
                                            }}
                                        >
                                            [{label || 'Link'}]
                                        </LinkPreview>
                                    );
                                })}
                            </Box>
                        )}

                        <Box sx={{ marginTop: 'var(--spacer-S)' }}>
                            <IconButton
                                size="small"
                                disableRipple
                                onClick={() => addLinkToCheckbox(index)}
                            >
                                <AddCircleIcon />&nbsp;<span style={{ fontSize: '0.85rem' }}>{t(TK.addLink)}</span>
                            </IconButton>
                        </Box>

                        <Box sx={{ marginTop: 'var(--spacer-M)' }}>
                            <AutocompleteTextField
                                label={t(TK.consents)}
                                options={allConsentKeys.map(k => consentsMap[k])}
                                multiSelect
                                values={allConsentKeys.filter(k => checkbox.consents[k]).map(k => consentsMap[k])}
                                onValuesChanged={valuesText => handleFieldChange('consentsCheckboxes', localValues.consentsCheckboxes.map((v, i) => i !== index ? v : {
                                    ...v,
                                    consents: Object.fromEntries(allConsentKeys.map(k => [k, valuesText.includes(consentsMap[k])]))
                                }))}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--spacer-S)'}}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={checkbox.isMandatory || false}
                                        onChange={(e) => handleCheckboxChange(index, 'isMandatory', e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={<Typography variant="body2">{t(TK.mandatoryConsent)}</Typography>}
                            />

                            <IconButton
                                size="small"
                                disableRipple
                                onClick={() => removeCheckbox(index)}
                                style={{ color: 'var(--danger-color)' }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Alert color="secondary" isOpen>
                {t(TK.consentManagementPageExplanation)}
            </Alert>
            <Typography variant="body2" sx={{ color: 'var(--text-muted)', marginBottom: 'var(--spacer-M)' }}>
                {t(TK.inlineEditHint)}
            </Typography>

            <hr/>

            {localValues.consentsCheckboxes.map((checkbox, index) => renderCheckboxItem(checkbox, index))}

            <Box sx={{ marginBottom: 'var(--spacer-M)' }}>
                <IconButton
                    size="small"
                    disableRipple
                    onClick={addCheckbox}
                >
                    <AddCircleIcon />&nbsp;<span style={{ fontSize: '0.85rem' }}>{t(TK.addConsentCheckbox)}</span>
                </IconButton>
            </Box>

            <Box
                sx={{
                    marginTop: 'var(--spacer-L)'
                }}
            >
                <FormGroup>
                    <Label for="privacyPolicyLink">{t(TK.privacyPolicyLink)}</Label>
                    <Input
                        id="privacyPolicyLink"
                        type="url"
                        value={localValues.privacyPolicyLink || ''}
                        onChange={(e) => handleFieldChange('privacyPolicyLink', e.target.value)}
                        placeholder="https://example.com/privacy"
                    />
                </FormGroup>

                <FormGroup>
                    <Label for="termsAndConditionsLink">{t(TK.termsAndConditionsLink)}</Label>
                    <Input
                        id="termsAndConditionsLink"
                        type="url"
                        value={localValues.termsAndConditionsLink || ''}
                        onChange={(e) => handleFieldChange('termsAndConditionsLink', e.target.value)}
                        placeholder="https://example.com/terms"
                    />
                </FormGroup>
            </Box>
        </Box>
    );
};

export default ConsentManagementSettings;
